import { ActionIcon, Box, Group, Tooltip } from "@mantine/core";
import { IconCopy, IconEye, IconMoodSad, IconPencil, IconTrash } from "@tabler/icons-react";
import {
  type DataTableColumn,
  type DataTableSortStatus,
  DataTable as MantineDataTable,
  type DataTableProps as MantineTableProps,
} from "mantine-datatable";
import type { ReactNode } from "react";
import type { Pagination } from "../../models/pagination";
import styles from "./DataTable.module.css";

type PaginationProps<T> =
  | (Required<Pick<MantineTableProps<T>, "onPageChange">> & { pagination: Pagination })
  | {
      pagination?: never;
      onPageChange?: never;
    };

export type DataTableProps<T> = {
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onDuplicate?: (row: T) => void;
  canDelete?: (row: T) => boolean;
  viewOnly?: boolean;
  renderAdditionalActions?: (row: T) => ReactNode;
} & Pick<
  MantineTableProps<T>,
  "records" | "columns" | "selectedRecords" | "onSelectedRecordsChange" | "onSortStatusChange" | "sortStatus" | "fetching"
> &
  PaginationProps<T>;

export function DataTable<T>({
  columns = [],
  sortStatus = {} as DataTableSortStatus<T>,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  canDelete,
  viewOnly = false,
  onSelectedRecordsChange,
  selectedRecords,
  renderAdditionalActions,
  pagination,
  onPageChange,
  ...props
}: DataTableProps<T>) {
  const adjustedColumns: DataTableColumn<T>[] = [
    ...columns,
    ...(onView || onEdit || onDelete
      ? [
          {
            accessor: "action",
            title: "Aktionen",
            width: "0%",
            textAlign: "right" as const,
            render: (row: T) => (
              <Group gap={4} wrap="nowrap" justify="right">
                {onView && (
                  <Tooltip label="Anzeigen">
                    <ActionIcon size="md" variant="subtle" color="zli-blue" onClick={() => onView(row)}>
                      <IconEye />
                    </ActionIcon>
                  </Tooltip>
                )}

                {onEdit && !viewOnly && (
                  <Tooltip label="Editieren">
                    <ActionIcon size="md" variant="subtle" color="zli-blue" onClick={() => onEdit(row)}>
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>
                )}
                {onDuplicate && !viewOnly && (
                  <Tooltip label="Duplizieren">
                    <ActionIcon size="md" variant="subtle" color="zli-blue" onClick={() => onDuplicate(row)}>
                      <IconCopy />
                    </ActionIcon>
                  </Tooltip>
                )}
                {onDelete && !viewOnly && (canDelete?.(row) ?? true) && (
                  <Tooltip label="Löschen">
                    <ActionIcon size="md" variant="subtle" color="red" onClick={() => onDelete(row)}>
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                )}
                {renderAdditionalActions?.(row)}
              </Group>
            ),
          },
        ]
      : []),
  ];

  const paginationProps = pagination
    ? { page: pagination.page, totalRecords: pagination.count, recordsPerPage: pagination.limit, onPageChange, paginationWithEdges: true }
    : {};

  return (
    <MantineDataTable<T>
      pinLastColumn
      withTableBorder
      borderRadius="sm"
      withColumnBorders
      withRowBorders
      striped
      noRecordsText="Keine Einträge gefunden"
      minHeight={props.records?.length ? 0 : 220}
      noRecordsIcon={
        <Box className={styles.noRecordsBox}>
          <IconMoodSad size={36} strokeWidth={1.5} />
        </Box>
      }
      columns={adjustedColumns}
      sortStatus={sortStatus}
      selectedRecords={viewOnly ? undefined : selectedRecords}
      onSelectedRecordsChange={viewOnly ? undefined : onSelectedRecordsChange}
      {...paginationProps}
      {...props}
    />
  );
}
